import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiService } from '../../services/api';
import { DashboardStatsResponse } from '../../types';
import './StatsDashboard.css';

function formatReferenceMonthLong(ym: string): string {
  const match = ym.match(/^(\d{4})-(\d{2})$/);
  if (!match) return ym;
  const y = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  const d = new Date(Date.UTC(y, monthIndex, 15));
  return d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

interface StatsDashboardProps {
  embedded?: boolean;
  onBackToLeads?: () => void;
  onOpenSettings?: () => void;
}

export const StatsDashboard: React.FC<StatsDashboardProps> = ({
  embedded = false,
  onBackToLeads,
  onOpenSettings,
}) => {
  const [searchParams] = useSearchParams();
  const { usuario, organizationId, isAdmin, isOwner, isSuperAdmin } = useAuth();
  const orgIdFromUrl = searchParams.get('orgId');
  const effectiveOrgId = isSuperAdmin && orgIdFromUrl ? orgIdFromUrl : organizationId;

  const [stats, setStats] = useState<DashboardStatsResponse | null>(null);
  const [viewingOrgName, setViewingOrgName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [viewMode, setViewMode] = useState<'todos'>('todos');
  const [chartYear, setChartYear] = useState<number | null>(null);
  const [referenceMonth, setReferenceMonth] = useState<string | null>(null);

  const loadStats = useCallback(async () => {
    if (!effectiveOrgId) {
      setError('Organização não encontrada');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const hasPeriod = chartYear != null && referenceMonth != null;
      const response = await apiService.getDashboardStats(
        effectiveOrgId,
        hasPeriod ? { year: chartYear, month: referenceMonth } : undefined
      );
      setStats(response);
      if (!hasPeriod) {
        setChartYear(response.chartYear);
        setReferenceMonth(response.referenceMonth);
      }
      if (isSuperAdmin && orgIdFromUrl) {
        const org = await apiService.getOrganization(effectiveOrgId);
        setViewingOrgName(org.name);
      } else {
        setViewingOrgName(null);
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao carregar estatísticas');
    } finally {
      setLoading(false);
    }
  }, [effectiveOrgId, isSuperAdmin, orgIdFromUrl, chartYear, referenceMonth]);

  useEffect(() => {
    loadStats();
    const interval = window.setInterval(loadStats, 60000);
    return () => window.clearInterval(interval);
  }, [loadStats]);

  useEffect(() => {
    setChartYear(null);
    setReferenceMonth(null);
    setStats(null);
    setSelectedUserId('');
    setLoading(true);
  }, [effectiveOrgId]);

  const maxDailyValue = useMemo(() => {
    const values = stats?.trend.dailyLeadsReceived.map((item) => item.value) || [];
    return Math.max(1, ...values);
  }, [stats]);

  const maxMonthlyValue = useMemo(() => {
    const values = stats?.trend.monthlyLeadsReceived.map((item) => item.value) || [];
    return Math.max(1, ...values);
  }, [stats]);

  const selectedUser = useMemo(() => {
    if (!selectedUserId || !stats?.byUser?.length) return null;
    return stats.byUser.find((u) => u.usuarioId === selectedUserId) ?? null;
  }, [selectedUserId, stats?.byUser]);

  const displayStats = useMemo(() => {
    if (!stats) return null;
    if (selectedUser) {
      const todayConversion = selectedUser.today.leadsAttended > 0
        ? Number(((selectedUser.today.leadsConverted / selectedUser.today.leadsAttended) * 100).toFixed(1))
        : 0;
      const monthConversion = selectedUser.month.leadsAttended > 0
        ? Number(((selectedUser.month.leadsConverted / selectedUser.month.leadsAttended) * 100).toFixed(1))
        : 0;
      const yesterdayConversion = selectedUser.yesterday.leadsAttended > 0
        ? Number(((selectedUser.yesterday.leadsConverted / selectedUser.yesterday.leadsAttended) * 100).toFixed(1))
        : 0;
      return {
        today: {
          leadsReceived: stats.today.leadsReceived,
          leadsAttended: selectedUser.today.leadsAttended,
          leadsConverted: selectedUser.today.leadsConverted,
          conversionRate: todayConversion,
        },
        yesterday: {
          leadsReceived: stats.yesterday.leadsReceived,
          leadsAttended: selectedUser.yesterday.leadsAttended,
          leadsConverted: selectedUser.yesterday.leadsConverted,
          conversionRate: yesterdayConversion,
        },
        month: {
          leadsReceived: stats.month.leadsReceived,
          leadsAttended: selectedUser.month.leadsAttended,
          leadsConverted: selectedUser.month.leadsConverted,
          conversionRate: monthConversion,
        },
        avgFirstResponseMinutes: selectedUser.avgFirstResponseMinutes,
      };
    }
    return {
      today: stats.today,
      yesterday: stats.yesterday,
      month: stats.month,
      avgFirstResponseMinutes: stats.avgFirstResponseMinutes,
    };
  }, [stats, selectedUser]);

  const byUserSorted = useMemo(() => {
    const list = stats?.byUser ?? [];
    return [...list].sort((a, b) => b.month.leadsConverted - a.month.leadsConverted);
  }, [stats?.byUser]);

  const yearOptions = useMemo(() => {
    const anchor = stats?.chartYear ?? new Date().getFullYear();
    const max = Math.max(anchor, new Date().getFullYear());
    const min = max - 7;
    const years: number[] = [];
    for (let y = min; y <= max + 1; y += 1) {
      years.push(y);
    }
    return years;
  }, [stats?.chartYear]);

  const referenceMonthLabel = stats ? formatReferenceMonthLong(stats.referenceMonth) : '';

  const responseTimeStatus = useMemo(() => {
    if (!stats) return 'neutral';
    const avg = displayStats?.avgFirstResponseMinutes ?? 0;
    const meta = stats.goals.firstResponseTimeMinutes;
    if (avg <= meta) return 'good';
    if (avg <= meta * 1.5) return 'warning';
    return 'bad';
  }, [stats, displayStats]);

  const backlogStatus = useMemo(() => {
    if (!stats) return 'neutral';
    const count = stats.backlogCount;
    const meta = stats.goals.maxBacklog;
    if (count <= meta) return 'good';
    if (count <= meta * 1.2) return 'warning';
    return 'bad';
  }, [stats]);

  if (loading && !stats) {
    return (
      <div className={`stats-dashboard-page ${embedded ? 'embedded' : ''}`}>
        <div className="stats-loading">Carregando estatísticas...</div>
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className={`stats-dashboard-page ${embedded ? 'embedded' : ''}`}>
        <div className="stats-error">
          <p>{error}</p>
          <button className="stats-retry-button" onClick={loadStats}>
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  if (!stats || !displayStats) {
    return null;
  }

  const handleYearChange = (nextYear: number) => {
    const monthPart = stats.referenceMonth.slice(5, 7);
    setChartYear(nextYear);
    setReferenceMonth(`${nextYear}-${monthPart}`);
  };

  const handleSelectMonthFromChart = (ym: string) => {
    const y = Number(ym.slice(0, 4));
    if (Number.isFinite(y)) setChartYear(y);
    setReferenceMonth(ym);
  };

  const handleMonthBarKeyDown = (e: React.KeyboardEvent, ym: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleSelectMonthFromChart(ym);
    }
  };

  const lastUpdated = new Date(stats.lastUpdatedAt).toLocaleString('pt-BR');
  const campaignMetrics = stats.campaignMetrics ?? {
    unsubscribesToday: 0,
    unsubscribesMonth: 0,
    dispatchesSentToday: 0,
    dispatchesSentMonth: 0,
  };

  const pipelineNow = stats.pipelineNow ?? { emNegociacao: 0, emProposta: 0 };

  const monthLeadsGoal = Math.max(1, stats.goals.leadsReceivedMonth);
  const monthGoalProgress = Math.round((stats.month.leadsReceived / monthLeadsGoal) * 100);
  const responseTimeGoal = stats.goals.firstResponseTimeMinutes;
  const responseTimeProgress = responseTimeGoal > 0
    ? Math.min(100, (responseTimeGoal / Math.max(displayStats.avgFirstResponseMinutes, 0.1)) * 100)
    : 100;
  const backlogProgress = stats.goals.maxBacklog > 0
    ? Math.min(100, (stats.backlogCount / stats.goals.maxBacklog) * 100)
    : 0;

  return (
    <div className={`stats-dashboard-page ${embedded ? 'embedded' : ''}`}>
      <header className="stats-dashboard-header">
        <div>
          <h1>Dashboard de Estatísticas</h1>
          <p>
            {viewingOrgName ? (
              <>Visão de <strong>{viewingOrgName}</strong></>
            ) : (
              'Visão operacional de leads e atendimento'
            )}
          </p>
        </div>
        <div className="stats-dashboard-header-actions">
          <select
            className="stats-user-select stats-year-select"
            value={chartYear ?? stats.chartYear}
            onChange={(e) => handleYearChange(Number(e.target.value))}
            title="Ano do gráfico mensal (clique em um mês para ver detalhes)"
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
          <select
            className="stats-user-select"
            value={viewMode}
            onChange={() => setViewMode('todos')}
            title="Modo de visualização"
          >
            <option value="todos">Todos</option>
          </select>
          {stats.byUser && stats.byUser.length > 0 && (
            <select
              className="stats-user-select"
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              title="Filtrar por atendente"
            >
              <option value="">Todos</option>
              {stats.byUser.map((u) => (
                <option key={u.usuarioId} value={u.usuarioId}>
                  {u.nome}
                </option>
              ))}
            </select>
          )}
          {!stats.byUser?.length && <span className="stats-dashboard-user">{usuario?.nome}</span>}
          {(isAdmin || isOwner) && !viewingOrgName && (
            embedded ? (
              <button
                type="button"
                className="stats-dashboard-config-link"
                onClick={onOpenSettings}
              >
                Configurar metas
              </button>
            ) : (
              <Link to="/settings" className="stats-dashboard-config-link">
                Configurar metas
              </Link>
            )
          )}
          {viewingOrgName ? (
            <Link to="/admin" className="stats-dashboard-back-link">
              Voltar ao Admin
            </Link>
          ) : (
            embedded ? (
              <button
                type="button"
                className="stats-dashboard-back-link"
                onClick={onBackToLeads}
              >
                Voltar para Leads
              </button>
            ) : (
              <Link to="/dashboard" className="stats-dashboard-back-link">
                Voltar para Leads
              </Link>
            )
          )}
        </div>
      </header>

      <section className="stats-kpi-grid">
        <article
          className="stats-kpi-card stats-kpi-card-pipeline"
          title="Leads com status Em negociação no Kanban de vendas (stock actual na organização)"
        >
          <h2>Em negociação (agora)</h2>
          <strong>{pipelineNow.emNegociacao}</strong>
          <span className="neutral">Todos os leads da org neste estado</span>
        </article>
        <article
          className="stats-kpi-card stats-kpi-card-pipeline"
          title="Leads com status Proposta no Kanban de vendas (stock actual na organização)"
        >
          <h2>Em proposta (agora)</h2>
          <strong>{pipelineNow.emProposta}</strong>
          <span className="neutral">Todos os leads da org neste estado</span>
        </article>
        <article className="stats-kpi-card" title={`Leads recebidos hoje: ${displayStats.today.leadsReceived}. Ontem: ${displayStats.yesterday.leadsReceived}`}>
          <h2>Leads recebidos hoje</h2>
          <strong>{displayStats.today.leadsReceived}</strong>
          <span className="neutral">Recebidos ontem: {displayStats.yesterday.leadsReceived}</span>
        </article>
        <article className={`stats-kpi-card stats-kpi-card-backlog stats-${backlogStatus}`} title={`Backlog: ${stats.backlogCount} / Meta: ${stats.goals.maxBacklog}`}>
          <h2>Leads atendidos hoje</h2>
          <strong>{displayStats.today.leadsAttended}</strong>
          <span className="neutral">Atendidos ontem: {displayStats.yesterday.leadsAttended}</span>
          <div className="stats-progress-bar">
            <div className="stats-progress-fill" style={{ width: `${Math.min(100, backlogProgress)}%` }} />
          </div>
          <span className="stats-progress-label">Backlog: {stats.backlogCount}</span>
        </article>
        <article className="stats-kpi-card" title={`Convertidos hoje: ${displayStats.today.leadsConverted}. Conversão: ${displayStats.today.conversionRate}%`}>
          <h2>Convertidos hoje</h2>
          <strong>{displayStats.today.leadsConverted}</strong>
          <span className="neutral">Conversão hoje: {displayStats.today.conversionRate}% (ontem: {displayStats.yesterday.conversionRate}%)</span>
        </article>
        <article className={`stats-kpi-card stats-kpi-card-response stats-${responseTimeStatus}`} title={`Tempo médio no mês exibido (${referenceMonthLabel}): ${displayStats.avgFirstResponseMinutes} min. Meta: ${responseTimeGoal} min`}>
          <h2>Tempo médio 1ª resposta</h2>
          <strong>{displayStats.avgFirstResponseMinutes} min</strong>
          <span className="neutral">Mês exibido: {referenceMonthLabel} · Meta: {responseTimeGoal} min</span>
          <div className="stats-progress-bar">
            <div className="stats-progress-fill" style={{ width: `${Math.min(100, responseTimeProgress)}%` }} />
          </div>
        </article>
        <article
          className="stats-kpi-card"
          title={`Descadastros via link hoje (opt-out ativos registrados hoje, fuso ${stats.timezone})`}
        >
          <h2>Descadastros hoje</h2>
          <strong>{campaignMetrics.unsubscribesToday}</strong>
          <span className="neutral">No mês exibido: {campaignMetrics.unsubscribesMonth}</span>
        </article>
        <article
          className="stats-kpi-card"
          title={`Disparos de campanha enviados com sucesso (WhatsApp), fuso ${stats.timezone}`}
        >
          <h2>Disparos enviados</h2>
          <strong>{campaignMetrics.dispatchesSentToday}</strong>
          <span className="neutral">
            Hoje · Mês ({referenceMonthLabel}): {campaignMetrics.dispatchesSentMonth}
          </span>
        </article>
      </section>

      <section className="stats-columns-grid">
        <article className="stats-panel">
          <h3>Dia</h3>
          <p>Leads recebidos por dia ({referenceMonthLabel})</p>
          <div className="stats-bar-list">
            {stats.trend.dailyLeadsReceived.map((item) => (
              <div key={item.date} className="stats-bar-item" title={`${item.date}: ${item.value} leads`}>
                <span>{item.date?.slice(-2)}</span>
                <div className="stats-bar-track">
                  <div
                    className="stats-bar-fill"
                    style={{ width: `${Math.round((item.value / maxDailyValue) * 100)}%` }}
                  />
                </div>
                <span className="stats-bar-value">{item.value}</span>
              </div>
            ))}
          </div>
        </article>

        <article className="stats-panel">
          <h3>Mês</h3>
          <p>Indicadores do mês selecionado (leads)</p>
          <div className="stats-highlight">
            <span>Meta do mês</span>
            <strong>{monthGoalProgress}% concluído</strong>
          </div>
          <div className="stats-month-gauges">
            <div className="stats-gauge" title="Leads recebidos no mês">
              <span className="stats-gauge-label">Recebidos</span>
              <div className="stats-gauge-bar">
                <div
                  className="stats-gauge-fill"
                  style={{ width: `${Math.min(100, (stats.month.leadsReceived / monthLeadsGoal) * 100)}%` }}
                />
              </div>
              <span className="stats-gauge-value">{stats.month.leadsReceived}</span>
            </div>
            <div className="stats-gauge" title="Leads atendidos no mês">
              <span className="stats-gauge-label">Atendidos</span>
              <div className="stats-gauge-bar">
                <div
                  className="stats-gauge-fill"
                  style={{ width: `${Math.min(100, (stats.month.leadsAttended / Math.max(stats.goals.leadsAttendedMonth, 1)) * 100)}%` }}
                />
              </div>
              <span className="stats-gauge-value">{stats.month.leadsAttended}</span>
            </div>
            <div className="stats-gauge" title="Leads convertidos no mês">
              <span className="stats-gauge-label">Convertidos</span>
              <div className="stats-gauge-bar">
                <div
                  className="stats-gauge-fill"
                  style={{ width: `${Math.min(100, (stats.month.leadsConverted / Math.max(stats.goals.leadsConvertedMonth, 1)) * 100)}%` }}
                />
              </div>
              <span className="stats-gauge-value">{stats.month.leadsConverted}</span>
            </div>
          </div>
          <ul>
            <li>Leads recebidos: {stats.month.leadsReceived}</li>
            <li>Leads atendidos: {stats.month.leadsAttended}</li>
            <li>Leads convertidos: {stats.month.leadsConverted}</li>
            <li>Conversão: {stats.month.conversionRate}%</li>
            <li>Backlog atual: {stats.backlogCount}</li>
            <li>Descadastros (link) no mês: {campaignMetrics.unsubscribesMonth}</li>
            <li>Disparos enviados no mês: {campaignMetrics.dispatchesSentMonth}</li>
          </ul>
        </article>

        <article className="stats-panel">
          <h3>Ano</h3>
          <p>Leads recebidos por mês ({stats.chartYear}) — clique para detalhar</p>
          <div className="stats-bar-list">
            {stats.trend.monthlyLeadsReceived.map((item) => (
              <div
                key={item.month}
                role="button"
                tabIndex={0}
                className={`stats-bar-item stats-bar-item-clickable${item.month === stats.referenceMonth ? ' stats-bar-item-selected' : ''}`}
                title={`${item.month}: ${item.value} leads — clique para ver este mês`}
                onClick={() => item.month && handleSelectMonthFromChart(item.month)}
                onKeyDown={(e) => item.month && handleMonthBarKeyDown(e, item.month)}
              >
                <span>{`M${item.month?.slice(-2)}`}</span>
                <div className="stats-bar-track">
                  <div
                    className="stats-bar-fill stats-bar-fill-year"
                    style={{ width: `${Math.round((item.value / maxMonthlyValue) * 100)}%` }}
                  />
                </div>
                <span className="stats-bar-value">{item.value}</span>
              </div>
            ))}
          </div>
          <div className="stats-highlight stats-highlight-year">
            <span>Meta conversão</span>
            <strong>{stats.goals.conversionRatePercent}%</strong>
          </div>
        </article>
      </section>

      {stats.byUser && stats.byUser.length > 0 && (
        <section className="stats-by-user-section">
          <h2>Desempenho por atendente</h2>
          <p className="stats-by-user-period">Métricas mensais: {referenceMonthLabel} (hoje e ontem permanecem no dia corrente)</p>
          <div className="stats-by-user-grid">
            {byUserSorted.map((u) => (
              <article key={u.usuarioId} className="stats-by-user-card" title={`${u.nome}: ${u.month.leadsAttended} atendidos, ${u.month.leadsConverted} convertidos`}>
                <h3>{u.nome}</h3>
                <div className="stats-by-user-metrics">
                  <div className="stats-by-user-row">
                    <span>Atendidos hoje</span>
                    <strong>{u.today.leadsAttended}</strong>
                  </div>
                  <div className="stats-by-user-row">
                    <span>Atendidos ontem</span>
                    <strong>{u.yesterday.leadsAttended}</strong>
                  </div>
                  <div className="stats-by-user-row">
                    <span>Atendidos mês</span>
                    <strong>{u.month.leadsAttended}</strong>
                  </div>
                  <div className="stats-by-user-row">
                    <span>Convertidos hoje</span>
                    <strong>{u.today.leadsConverted}</strong>
                  </div>
                  <div className="stats-by-user-row">
                    <span>Convertidos ontem</span>
                    <strong>{u.yesterday.leadsConverted}</strong>
                  </div>
                  <div className="stats-by-user-row">
                    <span>Convertidos mês</span>
                    <strong>{u.month.leadsConverted}</strong>
                  </div>
                  <div className="stats-by-user-row">
                    <span>Conversão mês</span>
                    <strong>
                      {u.month.leadsAttended > 0
                        ? `${((u.month.leadsConverted / u.month.leadsAttended) * 100).toFixed(1)}%`
                        : '0%'}
                    </strong>
                  </div>
                  <div className="stats-by-user-row">
                    <span>Tempo médio 1ª resp.</span>
                    <strong>{u.avgFirstResponseMinutes} min</strong>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      <footer className="stats-dashboard-footer">
        <span>Última atualização: {lastUpdated}</span>
        <span>
          Dados reais da organização (atualização a cada 60s, fuso: {stats.timezone}) · Mês analisado:{' '}
          {referenceMonthLabel}
        </span>
      </footer>
    </div>
  );
};
